import { ApiError, type ApiResponse } from "./types";

const TOKEN_KEY = "cinema.access_token";

export function getAccessToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY) ?? localStorage.getItem(TOKEN_KEY);
}

export function setAccessToken(token: string, remember = false): void {
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
  const store = remember ? localStorage : sessionStorage;
  store.setItem(TOKEN_KEY, token);
}

export function clearAccessToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  form?: Record<string, string>;
  auth?: boolean;
};

/**
 * 薄封装 fetch：同域 /api，Bearer token，统一错误。
 */
export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, form, auth = true, headers: initHeaders, ...rest } = options;
  const headers = new Headers(initHeaders);

  if (form) {
    headers.set("Content-Type", "application/x-www-form-urlencoded");
  } else if (body !== undefined && !(body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  if (auth) {
    const token = getAccessToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }

  let payload: BodyInit | undefined;
  if (form) {
    payload = new URLSearchParams(form).toString();
  } else if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    payload = JSON.stringify(body);
  }

  const res = await fetch(path.startsWith("/") ? path : `/${path}`, {
    ...rest,
    headers,
    body: payload,
    // 携带 resource_token Cookie（SSE 搜种依赖）；同源 / 代理下安全
    credentials: "include",
  });

  if (res.status === 401) {
    const mfa = res.headers.get("X-MFA-Required") === "true";
    if (!mfa) {
      clearAccessToken();
      // 通知 AuthProvider 同步清状态，避免 storage 已空但 UI 仍显示已登录
      window.dispatchEvent(new Event("cinema:unauthorized"));
    }
    let detail = "未授权";
    try {
      const errBody = (await res.json()) as { detail?: string };
      if (errBody.detail) detail = String(errBody.detail);
    } catch {
      /* ignore */
    }
    throw new ApiError(detail, 401, mfa);
  }

  if (!res.ok) {
    let detail = res.statusText || "请求失败";
    try {
      const errBody = (await res.json()) as {
        detail?: string | { msg?: string }[];
        message?: string;
      };
      if (typeof errBody.detail === "string") {
        detail = errBody.detail;
      } else if (Array.isArray(errBody.detail)) {
        detail = errBody.detail
          .map((d) => (typeof d === "string" ? d : d.msg || ""))
          .filter(Boolean)
          .join("；");
      } else if (errBody.message) {
        detail = errBody.message;
      }
    } catch {
      /* ignore */
    }
    throw new ApiError(detail, res.status);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return (await res.json()) as T;
}

/** 调用返回 schemas.Response 的接口，失败时抛出 message */
export async function apiResponse<T>(
  path: string,
  options?: RequestOptions,
): Promise<T> {
  const data = await apiRequest<ApiResponse<T>>(path, options);
  if (data && typeof data === "object" && "success" in data) {
    if (!data.success) {
      throw new ApiError(data.message || "操作失败", 400);
    }
    return data.data as T;
  }
  return data as T;
}
